import { ReactNode, useCallback } from 'react';
import { getCookie } from 'cookies-next';
import type { IncomingMessage } from 'node:http';

import { graphql } from '@/graphql/__generated/gql';
import useRegion from '@/hooks/providers/useRegion';
import { AUTH_COOKIE_KEY, SPACE } from '@/utils/constants/common';
import { prefetchRequest, useRequest } from '@/utils/request';
import { getLocale } from '@/utils/server/locale-utils';
import { getRegion } from '@/utils/server/region-utils';

import { parseLinks } from '../utils/parseLinks';
import { parseTokens, PropsObjectByTokenType } from '../utils/tokenParsing';

const cacheKey = 'translations';

type TranslationQuery = {
    [name: string]: unknown;
};

export type TranslateMethod = (
    key: string,
    query?: TranslationQuery | null,
    fallbackValue?: string,
) => string;

export type Translate = {
    t: TranslateMethod;
    tParsed: (
        key: string,
        props?: PropsObjectByTokenType,
        query?: TranslationQuery | null,
        fallbackValue?: string,
    ) => ReactNode[];
    tParsedLinks: (
        key: string,
        query?: TranslationQuery | null,
        fallbackValue?: string,
    ) => string | ReactNode;
};

const query = graphql(/* GraphQL */ `
    query Translations {
        translations
    }
`);

const useTranslation = () => {
    const { locale } = useRegion();
    const normalizedLocale = locale?.toLowerCase();
    const { data, isLoading } = useRequest({
        cacheKey: `${cacheKey}-${normalizedLocale}`,
        query,
        appName: 'user-profiles',
    });

    const t: Translate['t'] = useCallback(
        (key, query, fallbackValue = SPACE) => {
            // If the key is not found or loaded yet, return a fallback value
            // use a space if no fallback is provided to keep element height
            let token = (data?.translations?.[key] as string) ?? fallbackValue;

            if (query) {
                Object.keys(query).forEach((queryKey) => {
                    token = token.replaceAll(
                        new RegExp(`{\\s*${queryKey}}`, 'gm'),
                        query[queryKey] as string,
                    );
                });
            }

            return token;
        },
        [data],
    );

    const tParsed: Translate['tParsed'] = useCallback(
        (key, props, query, fallbackValue = SPACE) => {
            const string = t(key, query, fallbackValue);
            return parseTokens(string, props);
        },
        [t],
    );

    const tParsedLinks: Translate['tParsedLinks'] = useCallback(
        (key, query, fallbackValue = SPACE) => {
            const value = t(key, query, fallbackValue);
            const translationLinks = data?.translations?.[`${key}_links`];
            if (translationLinks && typeof translationLinks === 'string') {
                return parseLinks(translationLinks, key, value);
            }
            return value;
        },
        [data, t],
    );

    return {
        t,
        tParsed,
        tParsedLinks,
        isLoading,
        /** The raw key → copy map, for tools that list the keys (the Mosaic copy field). */
        translations: data?.translations as Record<string, unknown> | undefined,
    };
};

// Used with getServerSideProps
const prefetchTranslation = (req: IncomingMessage) => {
    const region = getRegion(req);
    const authCookie = getCookie(AUTH_COOKIE_KEY, { req });
    const locale = getLocale(req, !!authCookie, region).toLowerCase();
    return prefetchRequest({
        cacheKey: `${cacheKey}-${locale}`,
        query,
        appName: 'user-profiles',
        req,
    });
};

export { useTranslation as default, prefetchTranslation };
